import React, { FC, useState } from 'react';

import Avatars from 'components/UI/Avatars/Avatars';
import UILink from 'components/UI/Link';

import { ROUTE_CARD_DETAIL } from 'routes';
import { COLORS } from 'styles/constants';
import { CardSchema, ECardLayoutOptions, MessageSchema } from 'constants/graphqlTypes';
import { TEST_CARD_COMMON, getCardSourceLabelId } from 'constants/aqa/card';
import getAuthUser from 'helpers/getAuthUser';
import { getUserIdFromMessage } from 'graphQL/messages/helpers';
import ReactionButton from 'components/Messages/Message/Shared/ReactionButton';
import { StyledTokenWrap } from 'components/Messages/Message/styled';
import ReactionsToken from 'components/Messages/Message/ReactionsToken';
import { useMessagesChatData } from 'components/Messages/helpers/hooks';
import usePageType from 'helpers/usePageType';
import { REF_PLACEHOLDER } from 'components/Explore/Item/helpers';
import getStyles from 'helpers/getStyles';
import CardFooterMenu from 'components/UI/CardFooterMenu';
import { useReactionMenu } from 'components/Reactions/helpers/hooks';
import { StyledWrap, StyledFooter, StyledRelWrap, StyledProfilePicksFooter, StyledSourceAndMenuWrap } from './styled';

import AnimatedCardVisual from '../FirstUp/AnimatedCardVisual';
import Body from './Body';
import Source from './Source';
import Actions from '../Actions/Actions';
import { InnerSection } from './InnerSection';

interface ICommonProps {
  card: CardSchema;
  isDark?: boolean;
  showActivity?: boolean | null;
  contentEmoji?: string;
  message?: MessageSchema;
  disableReactionCallback?: (value: boolean) => void;
  dataTestId?: string;
  isProfilePicks?: boolean;
  fontColor?: string;
  hideFooterAvatars?: boolean;
  disableAnimation?: boolean;
  hideActionsAvatar?: boolean;
  hideActionsUserInfo?: boolean;
  hideTopAvatars?: boolean;
  isSpotlight?: boolean;
  isSmall?: boolean;
  isCarousel?: boolean;
  showAddReaction?: boolean;
}

const Common: FC<ICommonProps> = ({
  card,
  isDark = true,
  showActivity,
  contentEmoji,
  message,
  disableReactionCallback,
  dataTestId,
  isProfilePicks,
  fontColor,
  hideFooterAvatars = false,
  disableAnimation = false,
  hideActionsAvatar = false,
  hideActionsUserInfo = false,
  hideTopAvatars = false,
  isSpotlight = false,
  isSmall = false,
  isCarousel = false,
  showAddReaction = false,
}) => {
  const footerColor = isDark ? COLORS.white[100] : COLORS.brown.dark[100];
  const actionsFontColor = fontColor ?? footerColor;
  const { chatId } = useMessagesChatData();
  const { id, pickedByUsers } = card;
  const { userId: currentUserId } = getAuthUser();
  const userId = getUserIdFromMessage(message);
  const isMine = userId === currentUserId;
  const { isListDetail } = usePageType();
  const isPickedByDeletedUser =
    isListDetail && pickedByUsers?.totalCount === 1 && pickedByUsers?.items?.[0]?.id === REF_PLACEHOLDER;
  const isShowActivity = showActivity || isPickedByDeletedUser;

  const { cardStyle } = card;
  const { layout } = getStyles(cardStyle);

  const isPlainCard = layout === ECardLayoutOptions.Plain;
  const contentIds = {
    chatId,
    messageId: message?.id || '',
  };

  const {
    setIsReactionsModalOpen,
    setIsQuickReactionOpen,
    setReactionCoordinates,
    isReactionsModalOpen,
    isQuickReactionOpen,
  } = useReactionMenu();

  const [isReactionButtonMenuShown, setIsReactionButtonMenuShown] = useState(false);

  // Extract bg color value for animated wrapper background
  const { bgColor: rawBgColor } = card.cardStyle ?? {};
  const { colorValue } = rawBgColor ?? ({} as { colorValue?: string });

  return (
    <StyledWrap>
      <StyledRelWrap>
        <InnerSection isCarousel={isCarousel}>
          <Actions
            item={card}
            fontColor={actionsFontColor}
            isSmall={isSmall}
            isSpotlightInFocus={false}
            isReactionButtonMenuShown={isReactionButtonMenuShown}
            setIsReactionButtonMenuShown={setIsReactionButtonMenuShown}
            setIsReactionsModalOpen={setIsReactionsModalOpen}
            setIsQuickReactionOpen={setIsQuickReactionOpen}
            setReactionCoordinates={setReactionCoordinates}
            isProfilePicks={isProfilePicks}
            hideAvatar={hideActionsAvatar}
            hideUserInfo={hideActionsUserInfo}
            isSpotlight={isSpotlight}
            isCarousel={isCarousel}
            showAddReaction={showAddReaction}
          />
          {isShowActivity && !hideTopAvatars && (
            <UILink route={ROUTE_CARD_DETAIL} params={{ pickId: id }}>
              <Avatars limit={3} total={pickedByUsers?.totalCount ?? 0} users={pickedByUsers?.items ?? []} hideNumber />
            </UILink>
          )}
        </InnerSection>
        {disableAnimation ? (
          <Body card={card} dataTestId={dataTestId} />
        ) : (
          <AnimatedCardVisual
            card={card}
            dataTestId={dataTestId}
            colorValue={colorValue ?? 'white'}
            pauseAnimation={!!isQuickReactionOpen || !!isReactionsModalOpen || !!isReactionButtonMenuShown}
          />
        )}
        {contentEmoji && <ReactionButton emojiCode={contentEmoji} />}
        <StyledTokenWrap className="token-wrap" isRight={isMine}>
          <ReactionsToken contentIds={contentIds} isDark={isDark} />
        </StyledTokenWrap>
      </StyledRelWrap>
      {isProfilePicks ? (
        <StyledProfilePicksFooter isPlainCard={isPlainCard} color={footerColor}>
          <StyledSourceAndMenuWrap>
            <Source dataTestId={getCardSourceLabelId(dataTestId ?? TEST_CARD_COMMON)} card={card} />
            <CardFooterMenu card={card} color={footerColor} />
          </StyledSourceAndMenuWrap>
        </StyledProfilePicksFooter>
      ) : (
        <StyledFooter
          color={footerColor}
          className="card-footer"
          onTouchStart={() => disableReactionCallback?.(true)}
          onMouseDownCapture={() => disableReactionCallback?.(true)}
          onMouseUpCapture={() => disableReactionCallback?.(false)}
          onTouchEnd={() => disableReactionCallback?.(false)}
          onMouseLeave={() => disableReactionCallback?.(false)}
        >
          <Source dataTestId={getCardSourceLabelId(dataTestId ?? TEST_CARD_COMMON)} card={card} />
          {isShowActivity && !hideFooterAvatars && (
            <UILink route={ROUTE_CARD_DETAIL} params={{ pickId: id }}>
              <Avatars limit={3} total={pickedByUsers?.totalCount ?? 0} users={pickedByUsers?.items ?? []} hideNumber />
            </UILink>
          )}
        </StyledFooter>
      )}
    </StyledWrap>
  );
};

export default Common;
